/**
 * Validation schema for order preferences form using Yup
 * Unified validation for both create and edit operations
 */

import {
  type Day,
  type CutoffTime,
  type OrderPreferencesState,
  DAYS,
} from '@choco-admin/types';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export interface OrderPreferencesFormData {
  allowCustomProducts: boolean;
  mandatoryCustomerNumber: boolean;
  mandatoryDeliveryDate: boolean;
  enableProductComments: boolean;
  allowFractionalUnits: boolean;
  allowOnlyOneVariant: boolean;
  sortBy: OrderPreferencesState['sortBy'];
  cutoffType: OrderPreferencesState['cutoffType'];
  deliveryDays: Day[];
  cutoffTimes: Record<Day, CutoffTime>;
}

const validateCutoffTime = (value: string): boolean => {
  if (!value) return false;
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

export const orderPreferencesValidationSchema = yup.object().shape({
  allowCustomProducts: yup.boolean().required().default(false),
  mandatoryCustomerNumber: yup.boolean().required().default(false),
  mandatoryDeliveryDate: yup.boolean().required().default(true),
  enableProductComments: yup.boolean().required().default(false),
  allowFractionalUnits: yup.boolean().required().default(false),
  allowOnlyOneVariant: yup.boolean().required().default(false),
  sortBy: yup
    .string()
    .oneOf(['alphabetical', 'orderGuide'] as const)
    .required()
    .default('orderGuide'),
  cutoffType: yup
    .string()
    .oneOf(['strict', 'flexible', 'no_cutoff'] as const)
    .required()
    .default('strict'),
  deliveryDays: yup
    .array()
    .of(yup.mixed<Day>().oneOf(DAYS))
    .test(
      'at-least-one-day',
      'At least one delivery day must be selected',
      (value) => (value?.length ?? 0) > 0
    )
    .required()
    .default([]),
  cutoffTimes: yup
    .object()
    .shape(
      DAYS.reduce(
        (acc: Record<Day, yup.StringSchema>, day: Day) => {
          acc[day] = yup
            .string()
            .test(
              'valid-time',
              'Invalid time format. Must be HH:mm',
              (value: string | undefined) => {
                if (!value) return true;
                return validateCutoffTime(value);
              }
            )
            .when('cutoffType', {
              is: (type: string) => type !== 'no_cutoff',
              then: (schema) => schema.required('Cutoff time is required'),
              otherwise: (schema) => schema.optional(),
            });
          return acc;
        },
        {} as Record<Day, yup.StringSchema>
      )
    )
    .required()
    .default({}),
}) as yup.ObjectSchema<OrderPreferencesFormData>;

export const orderPreferencesResolver = yupResolver<OrderPreferencesFormData>(
  orderPreferencesValidationSchema
);
